import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SettingsGroup, SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  Caption,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FormFooter,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Separator,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import {
  LanguageSettings,
  PromptTemplateSettings,
} from '@eluve/frontend-feature-user-settings';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';
import { useBeforeUnload } from '@eluve/utility-hooks';

const EXAMPLE_INSTRUCTION_KEYS = [
  'settings.note.exampleRemoveLifestyle',
  'settings.note.exampleIncludeQuotes',
  'settings.note.examplePlanBullets',
  'settings.note.exampleFollowUpTimeline',
] as const;

const noteInstructionsFragment = graphql(`
  fragment NoteInstructions on TenantUserSettings {
    __typename
    userId
    tenantId
    customNoteInstructions
  }
`);

const updateCustomInstructionsMutation = graphql(
  `
    mutation UpdateCustomInstructions(
      $tenantId: uuid!
      $userId: uuid!
      $instructions: String!
    ) {
      updateTenantUserSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { customNoteInstructions: $instructions }
      ) {
        ...NoteInstructions
      }
    }
  `,
  [noteInstructionsFragment],
);

export const NoteSettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const toast = useToast();
  const analytics = useAnalytics('user_settings');
  const { t } = useTranslation();

  const { customNoteInstructions } =
    useCompleteFragment({
      fragment: noteInstructionsFragment,
      key: { tenantId, userId },
      strict: false,
    }) ?? {};

  const [instructions, setInstructions] = useState<string>(
    customNoteInstructions ?? '',
  );

  const isDirty =
    (Boolean(instructions) || Boolean(customNoteInstructions)) &&
    (instructions ?? null) !== (customNoteInstructions ?? null);

  useBeforeUnload(isDirty);

  const [setCustomInstructions] = useMutation(
    updateCustomInstructionsMutation,
    {
      onError: () => toast.error(t('settings.note.saveInstructionsFailed')),
    },
  );

  const saveInstructions = async () => {
    await setCustomInstructions({
      variables: { tenantId, userId, instructions },
      onCompleted: (data) => {
        if (data.updateTenantUserSettingsByPk) {
          analytics.capture('custom_instructions_update', {
            tenantId,
            userId,
          });
        }
      },
    });
  };

  return (
    <SettingsPageFrame title={t('settings.note.title')}>
      <SettingsGroup
        title={t('settings.note.defaults')}
        description={t('settings.note.defaultsDescription')}
      >
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldContent>
            <FieldLabel className="whitespace-nowrap">
              {t('settings.note.defaultTemplate')}
            </FieldLabel>
            <FieldDescription>
              {t('settings.note.defaultTemplateDescription')}
            </FieldDescription>
          </FieldContent>
          <div className="flex w-full max-w-60 justify-end">
            <PromptTemplateSettings className="w-full" />
          </div>
        </Field>
        <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
          <Separator className="my-0" />
          <LanguageSettings />
        </TenantOrUserFeatureFlaggedComponent>
      </SettingsGroup>

      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>{t('settings.note.customInstructions')}</FormTitle>
          <FormNewDescription>
            {t('settings.note.customInstructionsDescription')}
          </FormNewDescription>
        </FormHeader>
        <Textarea
          placeholder={t('settings.note.instructionsPlaceholder')}
          rows={4}
          className="min-h-25 bg-bg-primary"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <FormFooter className="gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={!isDirty}
            onClick={() => setInstructions(customNoteInstructions ?? '')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty}
            onClick={saveInstructions}
          >
            {t('settings.common.save')}
          </Button>
        </FormFooter>
        <Stack gap={2} align="start">
          <Caption
            size="3xs"
            weight="bold"
            color="supporting"
            className="block tracking-[2px]"
          >
            {t('settings.common.examples')}
          </Caption>
          <Stack gap={2} align="start">
            {EXAMPLE_INSTRUCTION_KEYS.map((exampleKey) => (
              <Button
                key={exampleKey}
                variant="outline"
                size="sm"
                onClick={() => setInstructions(t(exampleKey))}
              >
                {t(exampleKey)}
              </Button>
            ))}
          </Stack>
        </Stack>
      </FormSection>
    </SettingsPageFrame>
  );
};
